/**
 * A kanban card. Render only — receives task data + event handlers from the
 * parent column (which wraps this in `useSortable`). The card itself is no
 * longer registered with `useDraggable`; that previously conflicted with the
 * surrounding sortable on the same id, causing flicker and click→drag
 * misfires. Drag listeners live on the wrapper in `Column.tsx`.
 */

import type { Task } from '@friday/shared/realtime'
import { cn } from '@/lib/utils'
import { PRIORITIES } from '@friday/shared/realtime'
import type { PublicProfile } from '@/features/profiles/profiles'
import { AssigneeChip } from '@/components/user/AssigneeChip'
import { LabelChips } from '@/components/ui/LabelChips'

const PRIORITY_COLOR: Record<string, string> = {
  urgent: 'bg-red-500',
  high: 'bg-amber-500',
  medium: 'bg-blue-500',
  low: 'bg-surface-400',
}

interface CardProps {
  task: Task
  /** Profiles of the people who can be the assignee (FRIDAY-75). */
  profiles?: Record<string, PublicProfile>
  isSelected?: boolean
  isDragging?: boolean
  onOpen: (task: Task) => void
  /** Optional extra className — used to set width on the DragOverlay clone. */
  className?: string
}

export function Card({ task, profiles, isSelected, isDragging, onOpen, className }: CardProps) {
  return (
    <div
      onClick={(e) => {
        // Don't open the drawer if a drag just ended — dnd-kit considers the
        // pointer-up after a drag a "click".
        if (isDragging) return
        onOpen(task)
        e.stopPropagation()
      }}
      className={cn(
        'group cursor-pointer select-none rounded-md border bg-white dark:bg-surface-800 p-2.5 text-sm shadow-sm transition-shadow hover:shadow-md',
        'border-surface-200 dark:border-surface-700',
        isDragging && 'opacity-30',
        isSelected && 'ring-2 ring-brand-500',
        // The DragOverlay clone lives in a body portaled node without the
        // column's w-72 parent, so it needs an explicit width to look like
        // the source card and be visible at all.
        className,
      )}
      data-card
    >
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono text-[10px] text-surface-500">{task.key}</span>
        <span
          className={cn('h-1.5 w-1.5 rounded-full', PRIORITY_COLOR[task.priority] ?? 'bg-surface-400')}
          title={task.priority}
        />
      </div>
      <div className="mt-1 line-clamp-2 text-surface-900 dark:text-surface-50">{task.title}</div>
      {/* FRIDAY-71: labels show only when the task has some, so unlabelled cards keep their height. */}
      {!!task.labels?.length && <LabelChips labels={task.labels} max={3} className="mt-1.5" />}
      <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-surface-500">
        <div className="flex min-w-0 items-center gap-1.5">
          {task.subtaskCount > 0 && (
            <span title={`${task.subtaskDoneCount}/${task.subtaskCount} subtasks`}>
              ☑ {task.subtaskDoneCount}/{task.subtaskCount}
            </span>
          )}
          {task.storyPoints != null && (
            <span className="rounded bg-surface-100 dark:bg-surface-700 px-1 font-mono">
              {task.storyPoints}
            </span>
          )}
          {task.dueDate && (
            <span className={cn(isOverdue(task.dueDate) && 'text-red-500')}>{shortDate(task.dueDate)}</span>
          )}
        </div>
        {/* The assignee is a person, not a slice of a user id (FRIDAY-75).
            An unassigned task shows nothing, as before — the card keeps its
            height. */}
        {task.assigneeUserId && (
          <AssigneeChip
            userId={task.assigneeUserId}
            profiles={profiles ?? {}}
            size="xs"
            className="max-w-[7.5rem] shrink-0"
          />
        )}
      </div>
    </div>
  )
}

function isOverdue(due: string): boolean {
  return new Date(due) < new Date()
}

function shortDate(s: string): string {
  const d = new Date(s)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

void PRIORITIES
